import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  LinearProgress, Select, MenuItem, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete, Tooltip, Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutlined as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NoteAdd as NoteAddIcon,
  Flag as FlagIcon,
  ArrowForward as ArrowForwardIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  ReportProblem as ReportProblemIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPMDashboard, addFieldNote, setProjectPhase } from '../services/api';
import { format, isPast, isToday } from 'date-fns';

// ── Constants ──
const ACCENT_COLORS = {
  primary: '#0D2C54', success: '#1B5E20', error: '#B71C1C',
  warning: '#E65100', info: '#0D47A1',
};

const PHASE_NAMES = {
  1: 'Proposal', 2: 'Engineering', 3: 'Rough-in', 4: 'Pre-Final',
  5: 'Final', 6: 'Final Billing', 7: 'AfterCare',
};

const PHASE_COLORS = {
  1: '#78909C', 2: '#1565C0', 3: '#2E7D32', 4: '#F57C00',
  5: '#6A1B9A', 6: '#C62828', 7: '#00695C',
};

const SEVERITY_COLORS = { high: '#C62828', medium: '#E65100', low: '#F9A825' };
const SEVERITY_BG = { high: '#FFEBEE', medium: '#FFF3E0', low: '#FFFDE7' };

const HEALTH_DOTS = { green: '🟢', yellow: '🟡', red: '🔴' };

const STATUS_COLORS = {
  pending: { bg: '#FFF3E0', color: '#E65100' },
  in_progress: { bg: '#E3F2FD', color: '#1565C0' },
  completed: { bg: '#E8F5E9', color: '#2E7D32' },
  blocked: { bg: '#FFEBEE', color: '#C62828' },
};

// ── Management check (mirrors Layout.js) ──
// Joe Calabrese (93) added 2026-07-30 — keep in sync with Layout.js EXEC_MGMT_IDS.
const EXEC_IDS = new Set([1, 2, 3, 4, 19, 90, 93]);
const MGMT_IDS = new Set([5, 6, 7, 8, 34, 43, 77]);
const isMgmt = (user) =>
  EXEC_IDS.has(user?.id) || MGMT_IDS.has(user?.id) || user?.role === 'Management';

// ── Formatters ──
const fmtMoney = (v) => {
  if (!v || v === 0) return '$0';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d'); } catch { return '—'; }
};

// ── StatCard ──
const StatCard = ({ title, value, sub, color = 'primary', onClick }) => {
  const accent = ACCENT_COLORS[color] || color;
  return (
    <Paper
      elevation={0} onClick={onClick}
      sx={{
        p: 2.5, cursor: onClick ? 'pointer' : 'default',
        borderBottom: `3px solid ${accent}`, bgcolor: '#fff',
        transition: 'all 0.2s',
        '&:hover': onClick ? { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' } : {},
      }}
    >
      <Typography variant="overline" sx={{ color: '#8898A4', letterSpacing: 1.2, fontSize: '0.65rem', lineHeight: 1 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mt: 0.5, letterSpacing: '-0.5px' }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: '#8898A4', mt: 0.3, display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
};

// ── Section Header ──
const SectionHeader = ({ title, icon, count, action, onClick }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0D2C54', letterSpacing: '-0.3px' }}>
        {title}
      </Typography>
      {count !== undefined && count > 0 && (
        <Chip label={count} size="small" sx={{ fontWeight: 700, bgcolor: '#E3F2FD', color: '#1565C0', height: 22, fontSize: '0.75rem' }} />
      )}
    </Box>
    {action && (
      <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onClick} sx={{ textTransform: 'none', color: '#1565C0' }}>
        {action}
      </Button>
    )}
  </Box>
);

// ── Sortable table hook ──
function useSortable(defaultKey, defaultDir = 'asc') {
  const [orderBy, setOrderBy] = useState(defaultKey);
  const [order, setOrder] = useState(defaultDir);
  const handleSort = (key) => {
    if (orderBy === key) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(key); setOrder('asc'); }
  };
  const sortFn = useCallback((a, b) => {
    let av = a[orderBy], bv = b[orderBy];
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (typeof av === 'number' && typeof bv === 'number') return order === 'asc' ? av - bv : bv - av;
    if (typeof av === 'string') return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return 0;
  }, [orderBy, order]);
  return { orderBy, order, handleSort, sortFn };
}

// ══════════════════════════════════════════════════════════════════════════════
// ── PM Dashboard Page ──
// ══════════════════════════════════════════════════════════════════════════════
export default function PMDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPm, setSelectedPm] = useState(null);
  const [noteDialog, setNoteDialog] = useState({ open: false, projectId: null, projectName: '' });
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [showAllActions, setShowAllActions] = useState(false);

  const isManager = isMgmt(user);

  // ── Load data ──
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPMDashboard(selectedPm);
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('PM Dashboard load error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedPm]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Phase change handler ──
  const handlePhaseChange = async (projectId, newPhase) => {
    try {
      await setProjectPhase(projectId, newPhase);
      loadData(); // refresh
    } catch (err) {
      console.error('Phase change error:', err);
    }
  };

  // ── Quick note handler ──
  const handleSaveNote = async () => {
    if (!noteText.trim() || !noteDialog.projectId) return;
    setNoteSaving(true);
    try {
      await addFieldNote(noteDialog.projectId, { content: noteText.trim() });
      setNoteDialog({ open: false, projectId: null, projectName: '' });
      setNoteText('');
    } catch (err) {
      console.error('Note save error:', err);
    } finally {
      setNoteSaving(false);
    }
  };

  // ── Health flag handler ──
  const handleFlagProject = async (projectId) => {
    try {
      // Use the existing project update endpoint
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ health_status: 'red' }),
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Flag error:', err);
    }
  };

  // ── Sort for projects table ──
  const projectSort = useSortable('phase', 'asc');
  const sortedProjects = useMemo(() => {
    if (!data?.my_projects) return [];
    return [...data.my_projects].sort(projectSort.sortFn);
  }, [data?.my_projects, projectSort.sortFn]);

  // ── Render ──
  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  const { kpis, action_required, this_week, billing_alerts, my_projects, pm_list } = data;
  const visibleActions = showAllActions ? action_required : action_required.slice(0, 6);

  // Labor color
  const laborColor = kpis.labor_utilization_pct > 100 ? 'error'
    : kpis.labor_utilization_pct >= 85 ? 'warning' : 'success';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0D2C54', letterSpacing: '-0.3px' }}>
            My Projects
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
            {selectedPm && data.pm ? ` — Viewing ${data.pm.name}'s projects` : ''}
          </Typography>
        </Box>

        {/* Management PM selector */}
        {isManager && pm_list && pm_list.length > 0 && (
          <Select
            size="small"
            value={selectedPm || ''}
            displayEmpty
            onChange={(e) => setSelectedPm(e.target.value || null)}
            sx={{ minWidth: 200, bgcolor: '#fff' }}
          >
            <MenuItem value="">My Projects</MenuItem>
            {pm_list.map(pm => (
              <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {/* ── KPI Strip ── */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="My Projects" value={kpis.total_projects} color="primary" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="WIP Value" value={fmtMoney(kpis.total_wip_value)} color="info" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Labor Utilization"
            value={`${kpis.labor_utilization_pct.toFixed(1)}%`}
            color={laborColor}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Open A/R" value={fmtMoney(kpis.open_ar)} color="warning" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Overdue Tasks"
            value={kpis.overdue_tasks}
            color={kpis.overdue_tasks > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="At Risk"
            value={kpis.at_risk_count}
            color={kpis.at_risk_count > 0 ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      {/* ── Action Required ── */}
      <SectionHeader
        title="Action Required"
        icon={<ReportProblemIcon sx={{ color: action_required.length > 0 ? '#C62828' : '#2E7D32' }} />}
        count={action_required.length}
      />

      {action_required.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, bgcolor: '#E8F5E9', border: '1px solid #C8E6C9', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#2E7D32' }} />
            <Typography sx={{ color: '#2E7D32', fontWeight: 600 }}>All Clear — no projects need immediate attention</Typography>
          </Box>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2}>
            {visibleActions.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.project_id}>
                <Paper
                  elevation={0}
                  onClick={() => navigate(`/projects/${item.project_id}`)}
                  sx={{
                    p: 2, cursor: 'pointer', borderRadius: 2,
                    border: `1px solid ${SEVERITY_COLORS[item.severity]}20`,
                    bgcolor: SEVERITY_BG[item.severity],
                    borderLeft: `4px solid ${SEVERITY_COLORS[item.severity]}`,
                    transition: 'all 0.2s',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0D2C54', lineHeight: 1.3 }}>
                      {item.name}
                    </Typography>
                    <Chip
                      label={item.severity}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                        bgcolor: SEVERITY_COLORS[item.severity], color: '#fff', ml: 1, flexShrink: 0,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#4A7FB5', display: 'block' }}>
                    {item.client_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <WarningIcon sx={{ fontSize: 14, color: SEVERITY_COLORS[item.severity] }} />
                    <Typography variant="caption" sx={{ color: SEVERITY_COLORS[item.severity], fontWeight: 600 }}>
                      {item.reason}
                    </Typography>
                  </Box>
                  <Chip
                    label={PHASE_NAMES[item.phase] || `Phase ${item.phase}`}
                    size="small"
                    sx={{ mt: 1, height: 20, fontSize: '0.65rem', bgcolor: `${PHASE_COLORS[item.phase]}15`, color: PHASE_COLORS[item.phase], fontWeight: 600 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
          {action_required.length > 6 && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShowAllActions(!showAllActions)} sx={{ textTransform: 'none' }}>
                {showAllActions ? 'Show less' : `Show all ${action_required.length}`}
                {showAllActions ? <ExpandLessIcon sx={{ ml: 0.5 }} /> : <ExpandMoreIcon sx={{ ml: 0.5 }} />}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* ── This Week ── */}
      {this_week.length > 0 && (
        <>
          <SectionHeader
            title="Due This Week"
            icon={<ScheduleIcon sx={{ color: '#1565C0' }} />}
            count={this_week.length}
          />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2, maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Due</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this_week.map((task) => {
                  const overdue = task.due_date && isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date));
                  const sc = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
                  return (
                    <TableRow
                      key={task.task_id}
                      hover
                      onClick={() => navigate(`/projects/${task.project_id}`)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: overdue ? '#FFF8F8' : 'inherit',
                        borderLeft: overdue ? '3px solid #C62828' : 'none',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{task.task_name}</TableCell>
                      <TableCell sx={{ color: '#4A7FB5', fontSize: '0.8rem' }}>{task.project_name}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: overdue ? '#C62828' : '#4A7FB5', fontWeight: overdue ? 700 : 400 }}>
                        {fmtDate(task.due_date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.status.replace('_', ' ')}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: sc.bg, color: sc.color }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Billing Alerts ── */}
      {billing_alerts.length > 0 && (
        <>
          <SectionHeader
            title="Billing Alerts"
            icon={<AttachMoneyIcon sx={{ color: '#E65100' }} />}
            count={billing_alerts.length}
          />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2, maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Phase</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>A/R Balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem' }}>Contract</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billing_alerts.map((item) => (
                  <TableRow
                    key={item.project_id}
                    hover
                    onClick={() => navigate(`/projects/${item.project_id}`)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: item.phase === 6 ? '#FFF8F8' : 'inherit',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{item.name}</TableCell>
                    <TableCell sx={{ color: '#4A7FB5', fontSize: '0.8rem' }}>{item.client_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={PHASE_NAMES[item.phase]}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: item.phase === 6 ? '#FFEBEE' : `${PHASE_COLORS[item.phase]}15`,
                          color: item.phase === 6 ? '#C62828' : PHASE_COLORS[item.phase],
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: item.ar_balance > 50000 ? '#C62828' : '#0D2C54' }}>
                      {fmtMoney(item.ar_balance)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4A7FB5', fontSize: '0.8rem' }}>
                      {fmtMoney(item.contract_amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── My Projects — Full Table ── */}
      <SectionHeader
        title="All Projects"
        icon={<AssignmentIcon sx={{ color: '#0D2C54' }} />}
        count={my_projects.length}
        action="View WIP"
        onClick={() => navigate('/projects')}
      />

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2, maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                { id: 'name', label: 'Project' },
                { id: 'client_name', label: 'Client' },
                { id: 'phase', label: 'Phase' },
                { id: 'health_status', label: 'Health' },
                { id: 'wip_value', label: 'WIP Value', align: 'right' },
                { id: 'labor_pct', label: 'Labor %', align: 'right' },
                { id: 'days_in_phase', label: 'Days in Phase', align: 'right' },
                { id: 'task_progress', label: 'Tasks' },
                { id: 'actions', label: '', sortable: false },
              ].map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 700, color: '#4A7FB5', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                  {col.sortable === false ? col.label : (
                    <TableSortLabel
                      active={projectSort.orderBy === col.id}
                      direction={projectSort.orderBy === col.id ? projectSort.order : 'asc'}
                      onClick={() => projectSort.handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProjects.map((proj) => {
              const taskPct = proj.task_summary.total > 0
                ? Math.round(proj.task_summary.completed / proj.task_summary.total * 100) : 0;
              const laborClr = proj.labor_pct > 100 ? '#C62828' : proj.labor_pct >= 85 ? '#E65100' : '#2E7D32';
              const daysClr = proj.days_in_phase >= 90 ? '#C62828' : proj.days_in_phase >= 60 ? '#E65100' : '#4A7FB5';

              return (
                <TableRow
                  key={proj.id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F5F7FA' } }}
                >
                  <TableCell
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    sx={{ fontWeight: 600, fontSize: '0.8rem', maxWidth: 200 }}
                  >
                    <Box>
                      {proj.name}
                      {proj.primary_job_number && (
                        <Typography variant="caption" sx={{ color: '#8898A4', display: 'block', fontSize: '0.65rem' }}>
                          {proj.primary_job_number}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    sx={{ color: '#4A7FB5', fontSize: '0.8rem', maxWidth: 160 }}
                  >
                    {proj.client_name}
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select
                      value={proj.phase}
                      size="small"
                      onChange={(e) => handlePhaseChange(proj.id, e.target.value)}
                      sx={{
                        fontSize: '0.7rem', fontWeight: 600, height: 28,
                        bgcolor: `${PHASE_COLORS[proj.phase]}10`,
                        color: PHASE_COLORS[proj.phase],
                        '& .MuiSelect-select': { py: 0.3, px: 1 },
                        '& fieldset': { borderColor: `${PHASE_COLORS[proj.phase]}30` },
                      }}
                    >
                      {Object.entries(PHASE_NAMES).map(([k, v]) => (
                        <MenuItem key={k} value={parseInt(k)} sx={{ fontSize: '0.8rem' }}>{v}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/projects/${proj.id}`)}>
                    <Typography sx={{ fontSize: '1rem' }}>{HEALTH_DOTS[proj.health_status] || '⚪'}</Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0D2C54' }}
                  >
                    {fmtMoney(proj.wip_value)}
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    sx={{ fontWeight: 600, fontSize: '0.8rem', color: laborClr }}
                  >
                    {proj.labor_est > 0 ? `${proj.labor_pct.toFixed(0)}%` : '—'}
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    sx={{ fontWeight: proj.days_in_phase >= 90 ? 700 : 400, fontSize: '0.8rem', color: daysClr }}
                  >
                    {proj.days_in_phase > 0 ? proj.days_in_phase : '—'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/projects/${proj.id}`)} sx={{ minWidth: 120 }}>
                    {proj.task_summary.total > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={taskPct}
                          sx={{
                            flexGrow: 1, height: 6, borderRadius: 3,
                            bgcolor: '#E0E0E0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: proj.task_summary.overdue > 0 ? '#C62828' : '#2E7D32',
                              borderRadius: 3,
                            },
                          }}
                        />
                        <Typography variant="caption" sx={{ color: '#4A7FB5', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                          {proj.task_summary.completed}/{proj.task_summary.total}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#BDBDBD' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: 0.5 }}>
                    <Tooltip title="Add quick note">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNoteDialog({ open: true, projectId: proj.id, projectName: proj.name });
                        }}
                        sx={{ color: '#8898A4', '&:hover': { color: '#1565C0' } }}
                      >
                        <NoteAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {proj.health_status !== 'red' && (
                      <Tooltip title="Flag for management">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleFlagProject(proj.id); }}
                          sx={{ color: '#8898A4', '&:hover': { color: '#C62828' } }}
                        >
                          <FlagIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedProjects.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#8898A4' }}>
                  No active projects assigned
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Quick Note Dialog ── */}
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, projectId: null, projectName: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0D2C54' }}>
          Quick Note — {noteDialog.projectName}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            placeholder="Add a field note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNoteDialog({ open: false, projectId: null, projectName: '' }); setNoteText(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveNote} disabled={noteSaving || !noteText.trim()}>
            {noteSaving ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Loading overlay on refresh ── */}
      {loading && data && (
        <Box sx={{ position: 'fixed', top: 80, right: 20, zIndex: 1000 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}
